import { Service } from '../service';
import * as ThemeUtils from './utils';
import { getNestedValue } from '../../util/helpers';
import Vue from 'vue';
export class Theme extends Service {
    constructor(preset) {
        super();
        this.disabled = false;
        this.isDark = null;
        this.unwatch = null;
        this.vueMeta = null;
        const { dark, disable, options, themes } = preset[Theme.property];
        this.dark = Boolean(dark);
        this.defaults = this.themes = themes;
        this.options = options;
        if (disable) {
            this.disabled = true;
            return;
        }
        this.themes = {
            dark: this.fillVariant(themes.dark, true),
            light: this.fillVariant(themes.light, false)
        };
    }
    set css(val) {
        if (this.vueMeta) {
            if (this.isVueMeta23) {
                this.applyVueMeta23();
            }
            return;
        }
        this.checkOrCreateStyleElement() && (this.styleEl.innerHTML = val);
    }
    set dark(val) {
        const oldDark = this.isDark;
        this.isDark = val;
        oldDark != null && this.applyTheme();
    }
    get dark() {
        return Boolean(this.isDark);
    }
    applyTheme() {
        if (this.disabled)
            return this.clearCss();
        this.css = this.generatedStyles;
    }
    clearCss() {
        this.css = '';
    }
    init(root, ssrContext) {
        if (this.disabled)
            return;
        if (root.$meta) {
            this.initVueMeta(root);
        }
        else if (ssrContext) {
            this.initSSR(ssrContext);
        }
        this.initTheme(root);
    }
    setTheme(theme, value) {
        this.themes[theme] = Object.assign(this.themes[theme], value);
        this.applyTheme();
    }
    resetThemes() {
        this.themes.light = Object.assign({}, this.defaults.light);
        this.themes.dark = Object.assign({}, this.defaults.dark);
        this.applyTheme();
    }
    checkOrCreateStyleElement() {
        this.styleEl = document.getElementById('vuetify-theme-stylesheet');
        if (this.styleEl)
            return true;
        this.genStyleElement();
        return Boolean(this.styleEl);
    }
    fillVariant(theme = {}, dark) {
        const defaultTheme = this.themes[dark ? 'dark' : 'light'];
        return Object.assign({}, defaultTheme, theme);
    }
    genStyleElement() {
        if (typeof document === 'undefined')
            return;
        this.styleEl = document.createElement('style');
        this.styleEl.type = 'text/css';
        this.styleEl.id = 'vuetify-theme-stylesheet';
        if (this.options.cspNonce) {
            this.styleEl.setAttribute('nonce', this.options.cspNonce);
        }
        document.head.appendChild(this.styleEl);
    }
    initVueMeta(root) {
        this.vueMeta = root.$meta();
        if (this.isVueMeta23) {
            root.$nextTick(() => {
                this.applyVueMeta23();
            });
            return;
        }
        const metaKeyName = typeof this.vueMeta.getOptions === 'function' ? this.vueMeta.getOptions().keyName : 'metaInfo';
        const metaInfo = root.$options[metaKeyName] || {};
        root.$options[metaKeyName] = () => {
            metaInfo.style = metaInfo.style || [];
            const vuetifyStylesheet = metaInfo.style.find(s => s.id === 'vuetify-theme-stylesheet');
            if (!vuetifyStylesheet) {
                metaInfo.style.push({
                    cssText: this.generatedStyles,
                    type: 'text/css',
                    id: 'vuetify-theme-stylesheet',
                    nonce: (this.options || {}).cspNonce
                });
            }
            else {
                vuetifyStylesheet.cssText = this.generatedStyles;
            }
            return metaInfo;
        };
    }
    applyVueMeta23() {
        const { set } = this.vueMeta.addApp('vuetify');
        set({
            style: [{
                    cssText: this.generatedStyles,
                    type: 'text/css',
                    id: 'vuetify-theme-stylesheet',
                    nonce: this.options.cspNonce
                }]
        });
    }
    initSSR(ssrContext) {
        const nonce = this.options.cspNonce ? ` nonce="${this.options.cspNonce}"` : '';
        ssrContext.head = ssrContext.head || '';
        ssrContext.head += `<style type="text/css" id="vuetify-theme-stylesheet"${nonce}>${this.generatedStyles}</style>`;
    }
    initTheme(root) {
        if (typeof document === 'undefined')
            return;
        if (this.unwatch) {
            this.unwatch();
            this.unwatch = null;
        }
        root.$once('hook:created', () => {
            const obs = Vue.observable({
                themes: this.themes
            });
            this.unwatch = root.$watch(() => obs.themes, () => this.applyTheme(), {
                deep: true
            });
        });
        this.applyTheme();
    }
    get currentTheme() {
        const target = this.dark ? 'dark' : 'light';
        return this.themes[target];
    }
    get generatedStyles() {
        const theme = this.parsedTheme;
        const options = this.options || {};
        let css;
        if (options.themeCache != null) {
            css = options.themeCache.get(theme);
            if (css != null)
                return css;
        }
        css = ThemeUtils.genStyles(theme, options.customProperties);
        if (options.minifyTheme != null) {
            css = options.minifyTheme(css);
        }
        if (options.themeCache != null) {
            options.themeCache.set(theme, css);
        }
        return css;
    }
    get parsedTheme() {
        return ThemeUtils.parse(this.currentTheme || {}, undefined, getNestedValue(this.options, ['variations'], true));
    }
    get isVueMeta23() {
        return typeof this.vueMeta.addApp === 'function';
    }
}
Theme.property = 'theme';
